import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import type { PlanLinkDto } from '@tiffinify/contracts';
import { formatDay, formatPhone } from '@tiffinify/core';
import { BtnPrimary, BtnSecondary, Card, CardTitle, ErrorText, Helper, Hero, Icon, LoadError, Mono, PageLoader, TaskTitle, TopBar, useConfirm, type IconName } from '../components/ui';
import { post } from '../lib/api';
import { clock12, errorMessage, formatInr } from '../lib/format';
import { WhatsAppText } from '../lib/whatsapp';
import { useProfile, useSettings } from '../lib/queries';

/** The customer's "My plan" page: what they'll see, the message that carries the link, and WhatsApp. */
export function PlanLink() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { confirm, sheet: confirmSheet } = useConfirm();
  const profile = useProfile(id);
  const cutoff = useSettings().data?.pauseRequestCutoff ?? '23:59';
  // Asking twice gives the same link: there is one per customer.
  const q = useQuery({ queryKey: ['plan-link', id], queryFn: () => post<PlanLinkDto>(`/subscribers/${id}/plan-link`), staleTime: Infinity });
  const reset = useMutation({
    mutationFn: () => post<PlanLinkDto>(`/subscribers/${id}/plan-link?reset=1`),
    onSuccess: (res) => { qc.setQueryData(['plan-link', id], res); setNote('New link made. The old one no longer opens.'); },
  });
  const [note, setNote] = useState('');

  const p = profile.data;
  const name = p?.subscriber.name ?? '';
  const first = name.split(' ')[0] || 'them';
  const head = (
    <>
      <TopBar to={`/s/${id}`} label={name ? `Back to ${name}` : 'Back'} />
      <TaskTitle title="My plan page" sub={`${first}’s own page for their meals. No app, no sign-in.`} />
    </>
  );
  if (q.isError) return <main className="main task">{head}<LoadError what="the link" onRetry={() => void q.refetch()} /></main>;
  if (!q.data || !p) return <main className="main task">{head}<PageLoader label="Making the link" /></main>;

  const link = q.data.link;
  const sm = p.summary;
  const due = p.payment?.due ?? 0;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setNote('Link copied.'); } catch { setNote('Couldn’t copy. Press and hold the link to copy it.'); }
  };
  const share = async () => {
    try { await navigator.share({ title: `${p.subscriber.name}’s plan`, text: q.data.text }); } catch { /* closed the share sheet */ }
  };

  const can: Array<{ icon: IconName; text: string }> = [
    { icon: 'calendar', text: 'See meals left, the end date and their calendar' },
    { icon: 'rupee', text: due > 0 ? `Pay the ${formatInr(due)} due by UPI` : 'Pay by UPI whenever something is due' },
    { icon: 'pause', text: `Ask to pause a day, until ${clock12(cutoff)} the day before. You confirm it on Today` },
    { icon: 'refresh', text: 'Ask to renew on WhatsApp in the last week' },
  ];

  return (
    <main className="main task">
      {head}

      {sm && p.subscription && (
        <Hero label={`What ${first} sees`}>
          <div className="between"><Mono muted>What {first} sees</Mono><Mono muted>{p.subscription.planName}</Mono></div>
          <div className="number-row">
            <span className="big">{sm.left}</span>
            <span><b>{sm.left === 1 ? 'meal' : 'meals'} left</b><small>Ends {formatDay(sm.endDate)}</small></span>
          </div>
          <div className="hero-facts pair">
            <div className="hero-fact"><Mono muted>To pay</Mono><b className={due > 0 ? 'saffron' : undefined}>{due > 0 ? formatInr(due) : 'Paid up'}</b></div>
            <div className="hero-fact"><Mono muted>Pause requests</Mono><b>By {clock12(cutoff)}</b><small>the day before</small></div>
          </div>
        </Hero>
      )}

      <div className="stack">
        <BtnPrimary label={`Send to ${first} on WhatsApp`} icon="chat" href={q.data.wa} />
        <p className="sub center">To {formatPhone(p.subscriber.phone)}. You tap Send in WhatsApp; nothing goes out by itself.</p>
      </div>

      <Card pad>
        <CardTitle title="On their page they can" />
        <ul className="plan-link-can">
          {can.map((c) => <li key={c.icon}><span className="plan-link-can-icon" aria-hidden="true"><Icon name={c.icon} size={16} sw={2.2} /></span>{c.text}</li>)}
        </ul>
      </Card>

      <Card pad>
        <CardTitle title="The message" />
        <div className="bubble"><WhatsAppText text={q.data.text} /></div>
      </Card>

      <Card pad>
        <CardTitle title="Their link" />
        <p className="plan-link-url">{link}</p>
        <div className="plan-link-actions">
          <BtnSecondary sm label="Copy" icon="link" onClick={() => void copy()} />
          <BtnSecondary sm label="Open" icon="arrow" href={link} />
          {canShare && <BtnSecondary sm label="Share" icon="send" onClick={() => void share()} />}
        </div>
        {note && <p className="st-ok" role="status">{note}</p>}
        <Helper>The same link works for every plan {first} has with you, renewals included. It shows no phone numbers, notes or other customers.</Helper>
      </Card>

      <Card pad>
        <CardTitle title="Sent to the wrong person?" />
        <Helper>Make a new link. The old one stops working at once, and you send {first} the new one.</Helper>
        <ErrorText>{reset.error ? errorMessage(reset.error) : ''}</ErrorText>
        <BtnSecondary
          label="Make a new link"
          icon="refresh"
          busy={reset.isPending}
          onClick={async () => { if (await confirm({ title: 'Make a new link?', body: `The link ${first} has now will stop working. Send them the new one after.`, yes: 'Make a new link', danger: true })) reset.mutate(); }}
        />
      </Card>
      {confirmSheet}
    </main>
  );
}
